import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import { alpha } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import LeaderboardOutlinedIcon from '@mui/icons-material/LeaderboardOutlined';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import type { SvgIconComponent } from '@mui/icons-material';
import type { TemplateDef } from '../types';

const templateIcons: Record<string, SvgIconComponent> = {
  t1: LocalOfferOutlinedIcon,
  t2: TrendingUpOutlinedIcon,
  t3: CardGiftcardOutlinedIcon,
  t4a: ListAltOutlinedIcon,
  t4b: ReplayOutlinedIcon,
  t5: ConfirmationNumberOutlinedIcon,
  t6: LeaderboardOutlinedIcon,
  t7: SavingsOutlinedIcon,
  t8: LayersOutlinedIcon,
};

// Templates ocultos apenas na seleção; continuam disponíveis para importação e reaproveitamento do histórico.
const HIDDEN_TEMPLATE_IDS = new Set(['t7', 't8']);

interface TemplateSelectorProps {
  templates: TemplateDef[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

export default function TemplateSelector({ templates, selectedId, onSelect }: TemplateSelectorProps) {
  const visibleTemplates = templates.filter((tmpl) => !HIDDEN_TEMPLATE_IDS.has(tmpl.id));

  return (
    <Box>
      <Box sx={{ mb: 3, pb: 2.5, borderBottom: '1px solid #e2e8f0' }}>
        <Typography component="h2" sx={{ fontSize: 18, fontWeight: 700, color: '#0f172a', lineHeight: 1.4 }}>
          Selecione o Template
        </Typography>
        <Typography sx={{ fontSize: 13, color: '#64748b', mt: 0.5 }}>
          Escolha o tipo de mecânica promocional para carregar os campos correspondentes.
        </Typography>
      </Box>
      <Grid container spacing={2}>
        {visibleTemplates.map((tmpl) => {
          const Icon = templateIcons[tmpl.id] ?? LocalOfferOutlinedIcon;
          const isSelected = selectedId === tmpl.id;
          return (
            <Grid key={tmpl.id} size={{ xs: 12, sm: 6, lg: 4 }}>
              <ButtonBase
                onClick={() => onSelect(tmpl.id)}
                disableRipple
                aria-pressed={isSelected}
                sx={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  textAlign: 'left',
                  width: '100%',
                  height: '100%',
                  p: 2.5,
                  gap: 1.5,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: isSelected ? tmpl.color : '#e2e8f0',
                  bgcolor: isSelected ? alpha(tmpl.color, 0.04) : '#fff',
                  boxShadow: isSelected ? `0 0 0 3px ${alpha(tmpl.color, 0.12)}` : '0 1px 2px rgba(15, 23, 42, 0.04)',
                  overflow: 'hidden',
                  transition: 'border-color 0.2s, box-shadow 0.2s, transform 0.2s',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: '0 0 auto 0',
                    height: 3,
                    bgcolor: tmpl.color,
                    opacity: isSelected ? 1 : 0,
                    transition: 'opacity 0.2s',
                  },
                  '&:hover, &.Mui-focusVisible': {
                    borderColor: alpha(tmpl.color, 0.6),
                    boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
                    transform: 'translateY(-2px)',
                  },
                  '&:hover::before, &.Mui-focusVisible::before': { opacity: 1 },
                  '&:hover .template-icon': { bgcolor: tmpl.color, color: '#fff' },
                  '&:hover .template-cta': { color: tmpl.color, gap: 1 },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                  <Box
                    className="template-icon"
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      bgcolor: isSelected ? tmpl.color : alpha(tmpl.color, 0.1),
                      color: isSelected ? '#fff' : tmpl.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      transition: 'background-color 0.2s, color 0.2s',
                    }}
                  >
                    <Icon sx={{ fontSize: 22 }} />
                  </Box>
                  {isSelected ? (
                    <CheckCircleIcon sx={{ color: tmpl.color, fontSize: 22 }} />
                  ) : (
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: '#94a3b8',
                        bgcolor: '#f1f5f9',
                        borderRadius: 1,
                        px: 1,
                        py: 0.25,
                        letterSpacing: '0.5px',
                        textTransform: 'uppercase',
                      }}
                    >
                      {tmpl.number}
                    </Typography>
                  )}
                </Box>

                <Box sx={{ flexGrow: 1 }}>
                  <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#0f172a', lineHeight: 1.4 }}>
                    {tmpl.name}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: '#64748b', lineHeight: 1.6, mt: 0.5 }}>
                    {tmpl.description}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    pt: 1.5,
                    borderTop: '1px dashed #e2e8f0',
                  }}
                >
                  <Typography
                    component="span"
                    sx={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: tmpl.color,
                      bgcolor: alpha(tmpl.color, 0.08),
                      borderRadius: 999,
                      px: 1.25,
                      py: 0.25,
                      lineHeight: 1.6,
                    }}
                  >
                    {tmpl.mechanicsBadge}
                  </Typography>
                  <Box
                    className="template-cta"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      fontSize: 12,
                      fontWeight: 600,
                      color: isSelected ? tmpl.color : '#94a3b8',
                      whiteSpace: 'nowrap',
                      transition: 'color 0.2s, gap 0.2s',
                    }}
                  >
                    Selecionar
                    <ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />
                  </Box>
                </Box>
              </ButtonBase>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
